import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from 'react-i18next';

const { FiCheckCircle, FiUsers, FiHeart, FiTarget, FiBook, FiGlobe, FiShield, FiMessageCircle, FiTrendingUp } = FiIcons;

const About = () => {
  const { t } = useTranslation();

  const values = [
    {
      icon: FiCheckCircle,
      title: t('Professionalità'),
      description: t('Esperienza consolidata e approccio metodico in ogni aspetto della gestione immobiliare')
    },
    {
      icon: FiUsers,
      title: t('Trasparenza e Fiducia'),
      description: t('Comunicazione chiara,rapporti di fiducia e totale trasparenza in ogni operazione')
    },
    {
      icon: FiTrendingUp,
      title: t('Innovazione Continua'),
      description: t('Utilizzo di tecnologie avanzate e strategie di pricing dinamico per ottimizzare i risultati')
    },
    {
      icon: FiMessageCircle,
      title: t('Comunicazione Efficace'),
      description: t('Assistenza dedicata e multilingue per ospiti e proprietari disponibile 24/7')
    },
    {
      icon: FiShield,
      title: t('Affidabilità'),
      description: t('Gestione completa e accurata di ogni aspetto,dalla manutenzione alla risoluzione dei problemi')
    },
    {
      icon: FiHeart,
      title: t('Passione per i Dettagli'),
      description: t('Cura meticolosa di ogni aspetto dell\'esperienza,dalle pulizie alla presentazione degli immobili')
    }
  ];

  const background = [
    {
      icon: FiBook,
      title: t('Insegnamento'),
      description: t('Esperienza nel campo dell\'educazione e formazione')
    },
    {
      icon: FiGlobe,
      title: t('Giornalismo'),
      description: t('Background nel giornalismo e comunicazione')
    },
    {
      icon: FiGlobe,
      title: t('Gestione Linguistica'),
      description: t('Competenze nella gestione di progetti linguistici')
    },
    {
      icon: FiTarget,
      title: t('Progetti Internazionali'),
      description: t('Esperienza in progetti e collaborazioni internazionali')
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-warm-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-warm-900 mb-6"
            >
              {t('Chi Siamo')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-warm-700 mb-8 max-w-3xl mx-auto"
            >
              {t('La nostra missione è offrire una gestione completa "chiavi in mano" per proprietari di immobili in affitto breve')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-6">{t('La Nostra Missione')}</h2>
              <p className="text-lg text-warm-600 mb-6">
                {t('Il nostro obiettivo è')} <strong>{t('massimizzare i guadagni')}</strong> {t('dei proprietari di immobili senza che debbano occuparsene personalmente. Offriamo una gestione completa "chiavi in mano" che libera i proprietari da ogni preoccupazione legata alla gestione degli affitti brevi.')}
              </p>
              <p className="text-lg text-warm-600 mb-6">
                {t('Crediamo che ogni proprietario meriti di godere dei benefici economici del proprio immobile senza dover dedicare tempo e energie alla gestione quotidiana.')}
              </p>
              <div className="bg-primary-50 p-6 rounded-lg">
                <div className="flex items-center space-x-3 mb-3">
                  <SafeIcon icon={FiTarget} className="h-6 w-6 text-primary-600" />
                  <h3 className="text-lg font-semibold text-warm-900">{t('Il Nostro Obiettivo')}</h3>
                </div>
                <p className="text-warm-700">
                  {t('Massimizzare i guadagni senza che il proprietario debba occuparsene')}
                </p>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }} 
              viewport={{ once: true }}
              className="bg-warm-50 rounded-lg p-8 flex items-center justify-center"
            >
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752009564982-photo_5999181830990974370_w.jpg" 
                alt="Elvira Vigliano Property Manager" 
                className="w-64 h-64 object-cover rounded-full border-4 border-primary-200 shadow-xl" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-4">{t('I Nostri Valori')}</h2>
            <p className="text-xl text-warm-600 max-w-2xl mx-auto">{t('I principi che guidano ogni nostra azione')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200"
              >
                <div className="bg-primary-600 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                  <SafeIcon icon={value.icon} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-warm-900 mb-4">{value.title}</h3>
                <p className="text-warm-600 text-lg">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Elvira's Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6 }} 
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-warm-50 rounded-lg p-8 h-96 flex items-center justify-center">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753287373694-blob" 
                  alt="Villa frontemare Monterosso gestito da Elvira Vigliano" 
                  className="w-full h-full object-cover rounded-lg shadow-lg" 
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white p-3 rounded-full border-4 border-primary-100 shadow-lg">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752009564982-photo_5999181830990974370_w.jpg" 
                  alt="Logo Elvira Vigliano" 
                  className="w-20 h-20 rounded-full" 
                />
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }} 
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-6">{t('Perché Lo Faccio')}</h2>
              <p className="text-lg text-warm-600 mb-6">
                {t("Il mio background spazia dall'")}<strong>{t('insegnamento')}</strong> {t('al')} <strong>{t('giornalismo')}</strong>,{t('dalla')} <strong>{t('gestione linguistica')}</strong> {t('ai')} <strong>{t('progetti internazionali')}</strong>. {t('Questa varietà di esperienze mi ha dato una prospettiva unica sulla gestione e l\'ottimizzazione dei processi.')}
              </p>
              <p className="text-lg text-warm-600 mb-6">
                {t("La mia passione per le")} <strong>{t('persone')}</strong>,{t("l'")}<strong>{t('problem-solving')}</strong> {t('e la')} <strong>{t('valorizzazione degli spazi')}</strong> {t('mi ha naturalmente portata verso la gestione immobiliare,dove posso combinare competenze tecniche e relazionali per creare valore reale per i proprietari.')}
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                {background.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={item.icon} className="h-5 w-5 text-primary-600" />
                    <span className="text-warm-700 text-sm">{item.title}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('Vuoi Conoscerci Meglio?')}</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              {t('Contattaci per una consulenza gratuita e scopri come possiamo aiutarti')}
            </p>
            <a 
              href="mailto:elviraviglianopm@gmail.com" 
              className="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-warm-50 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {t('Contattaci Ora')}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;